<script setup lang="ts">
  import { BottomSheet, Calendar, CalendarSize, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { addDaysToDate } from '@travelata-libs/utils';
  import { ref } from 'vue';
  import { DateFromFlexChips } from '@/features/searchForm/dateFrom';
  import MobilePrimaryButton from '@/shared/ui/MobilePrimaryButton.vue';
  import { useMobileSearchFlowContext } from '../../../model/useMobileSearchFlow';

  const flow = useMobileSearchFlowContext();
  const bottomSheetRef = ref<null | { close: () => Promise<void> }>(null);

  const minDate = addDaysToDate(new Date(), 1);

  const onNext = async () => {
    await bottomSheetRef.value?.close();
  };
</script>

<template>
  <BottomSheet
    ref="bottomSheetRef"
    root-class="mobile-calendar-sheet"
    content-class="mobile-calendar-sheet__content"
    :animated="true"
    :icon-closable="true"
    height="95dvh"
    @close="flow.closeCalendar"
  >
    <template #header>
      <div class="mobile-calendar-sheet__head">
        <Typo
          :variant="TypoVariant.Heading5"
          tag="span"
        >
          {{ flow.texts.labels.date }}
        </Typo>
      </div>
    </template>

    <div
      class="mobile-calendar-sheet__calendar"
      data-testid="search-form-mobile-calendar"
    >
      <Calendar
        mobile
        data-testid="mobile-date-calendar-full"
        :model-value="flow.dateFrom.anchorIso.value"
        :size="CalendarSize.M"
        :range="false"
        :flexible-days="flow.dateFrom.flexibleForCalendar.value"
        :with-footer="false"
        :double="true"
        :scroll="true"
        :min-date="minDate"
        :max-date="flow.dateFrom.maxDate.value"
        @date-click="flow.onDateClick"
      />
    </div>

    <template #footer>
      <div class="mobile-calendar-sheet__actions">
        <DateFromFlexChips
          class="mobile-calendar-sheet__flex"
          :value="flow.dateFrom.flexible.value"
          @select="flow.onFlexSelect"
        />
        <MobilePrimaryButton
          testid="search-form-mobile-calendar-next"
          @click="onNext"
        >
          {{ flow.primaryTexts.next }}
        </MobilePrimaryButton>
      </div>
    </template>
  </BottomSheet>
</template>

<style scoped lang="scss">
  .mobile-calendar-sheet {
    &__head {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxs);
      padding-top: var(--spacing-xxs);
      text-align: center;
      width: 100%;
    }

    &__calendar {
      height: 95dvh;
      width: 100%;
    }

    &__actions {
      border-top-left-radius: var(--rounding-xl);
      border-top-right-radius: var(--rounding-xl);
      box-shadow: var(--shadow-s);
      display: flex;
      flex-direction: column;
      gap: var(--spacing-s);
      padding: var(--spacing-s) var(--spacing-l) var(--spacing-m) var(--spacing-l);
      width: 100%;
    }

    &__flex {
      justify-content: center;
    }
  }
</style>

<style lang="scss">
  .mobile-calendar-sheet .mobile-calendar-sheet__content {
    overflow: hidden;
  }

  .mobile-calendar-sheet .tl-bottom-sheet__footer {
    bottom: 0;
    padding: 0;
    position: absolute;
    width: 100%;
  }
</style>
